import { useEffect, useMemo } from 'react'
import { useFormContext, useWatch, type FieldValues } from 'react-hook-form'
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form'
import {
  SearchableSelect,
  type SearchableSelectOption,
} from '@/shared/components/searchable-select/SearchableSelect'
import { useCountries } from '@/shared/hooks/useCountries'

interface LocationFieldsProps {
  /**
   * Prefijo del path de los campos en el form. Default `'address'` (un único
   * objeto `address`). Para arreglos de direcciones usar `addresses.0`, etc.
   */
  name?: string
  /** Solo-lectura: deshabilita los tres selectores. */
  disabled?: boolean
}

/**
 * Selector en cascada País → Provincia → Municipio enlazado a los campos
 * `<name>.countryId/provinceId/municipalityId` del form. Autocontenido: lee el
 * form vía contexto, por lo que los wizards (organización/negocio) y el form de
 * edición lo montan sin duplicar la lógica. Los catálogos vienen de `useCountries`.
 *
 * Renderiza tres `FormItem` como hijos directos para encajar en el grid de dos
 * columnas. No se usa `FormControl` porque envuelve con un Radix `Slot` que
 * reenviaría `ref`/aria al `Popover` (sin nodo DOM); el estado inválido se pasa
 * con la prop `invalid` del `SearchableSelect`.
 *
 * El scalar `id` llega como string desde el gateway; aquí se convierte a number
 * porque el form guarda IDs numéricos (ver `addressSchema`).
 */
export function LocationFields({ name = 'address', disabled }: LocationFieldsProps = {}) {
  const { control, setValue } = useFormContext<FieldValues>()
  const { countries, loading, error } = useCountries()

  const countryField = `${name}.countryId`
  const provinceField = `${name}.provinceId`
  const municipalityField = `${name}.municipalityId`

  const countryId = useWatch({ control, name: countryField }) as number | undefined
  const provinceId = useWatch({ control, name: provinceField }) as number | undefined

  const selectedCountry = useMemo(
    () => countries.find((c) => Number(c.id) === countryId),
    [countries, countryId],
  )
  const selectedProvince = useMemo(
    () => selectedCountry?.provinces.find((p) => Number(p.id) === provinceId),
    [selectedCountry, provinceId],
  )

  const countryOptions = useMemo<SearchableSelectOption[]>(
    () => countries.map((c) => ({ value: Number(c.id), label: c.name, keywords: c.code })),
    [countries],
  )
  const provinceOptions = useMemo<SearchableSelectOption[]>(
    () =>
      selectedCountry?.provinces.map((p) => ({
        value: Number(p.id),
        label: p.name,
        keywords: p.code,
      })) ?? [],
    [selectedCountry],
  )
  const municipalityOptions = useMemo<SearchableSelectOption[]>(
    () =>
      selectedProvince?.municipalities.map((m) => ({
        value: Number(m.id),
        label: m.name,
        keywords: m.code,
      })) ?? [],
    [selectedProvince],
  )

  // País único (ej. Cuba) → preseleccionar para que el usuario solo elija
  // provincia y municipio.
  useEffect(() => {
    if (!countryId && countries.length === 1) {
      setValue(countryField, Number(countries[0].id), { shouldDirty: false })
    }
  }, [countries, countryId, countryField, setValue])

  if (error) {
    return (
      <p className="text-xs text-danger sm:col-span-2">
        No pudimos cargar las ubicaciones. Intenta de nuevo más tarde.
      </p>
    )
  }

  return (
    <>
      <FormField
        control={control}
        name={countryField}
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel required>País</FormLabel>
            <SearchableSelect
              value={field.value}
              onChange={(value) => {
                field.onChange(value)
                setValue(provinceField, 0)
                setValue(municipalityField, 0)
              }}
              options={countryOptions}
              placeholder="Selecciona un país"
              disabled={disabled}
              loading={loading}
              invalid={!!fieldState.error}
            />
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={provinceField}
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel required>Provincia</FormLabel>
            <SearchableSelect
              value={field.value}
              onChange={(value) => {
                field.onChange(value)
                setValue(municipalityField, 0)
              }}
              options={provinceOptions}
              placeholder="Selecciona una provincia"
              emptyMessage="Selecciona primero un país"
              disabled={disabled || !countryId}
              loading={loading}
              invalid={!!fieldState.error}
            />
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={municipalityField}
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel required>Municipio</FormLabel>
            <SearchableSelect
              value={field.value}
              onChange={(value) => field.onChange(value)}
              options={municipalityOptions}
              placeholder="Selecciona un municipio"
              emptyMessage="Selecciona primero una provincia"
              disabled={disabled || !provinceId}
              loading={loading}
              invalid={!!fieldState.error}
            />
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  )
}
